import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@amzn/meridian/esm/components/button";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Loader from "@amzn/meridian/esm/components/loader";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import InformSummarizeModal from "./InformSummarizeModal";
import SummarizeBoxIndicator from "./SummarizeBoxIndicator";
import aimsTheme from "../../meridian-theme-aims.json";
import star from "../../images/star.svg";
import errorIcon from "../../images/error_icon.svg";
import zoomSdk from "@zoom/appssdk";
import ServerNotRespondingModal from "../common/ServerNotRespondingModal";
import LogManager from "../utils/LogManager";
import { SUMMARIZE_STATES, RTMS_REQUESTER_DEFAULT_NAME } from "../../constants";
import { fetchRtmsSessionStatus, SESSION_STATUS, SESSION_STATUS_MESSAGE } from "../utils/RtmsSessionStatusUtils";
import { fetchSummaries } from "../utils/SummaryUtil";
import { fetchRtmsStatusUpdate } from "../utils/RtmsStatusUpdateUtil";
import RtmsErrorModal from "./RtmsErrorModal";
import ProductFeedback from "../common/feedback/ProductFeedback";
import { FEEDBACK_PAGE } from "../common/constants";

/**
 * This component is shown to the meeting host when AiMS is available for the meeting.
 *
 * Key Features:
 * - Renders a "Summarize" button to start RTMS.
 * - On first click, it shows an informational modal (`InformSummarizeModal`) to confirm the user understands the consent.
 * - On subsequent clicks, it triggers `startRTMS` via Zoom SDK.(TODO: will update to call backend api)
 * - Once summarization is active, the UI switches to an "End" button which stops RTMS.
 * - If RTMS start/stop fails, a `ServerNotRespondingModal` is shown for error feedback.
 * - When summarization ends, a temporary confirmation banner (`SummarizeStopped`) appears for 2 minutes.
 * - A `SummarizingIndicator` is shown during active summarization.
 * 
 * State Management:
 * - Uses SUMMARIZE_STATES for unified state management instead of multiple boolean flags
 * - Handles special cases like MULTI_MEETING_FOUND_FROM_OUTLOOK warnings
 * - Manages RTMS session lifecycle through Zoom SDK events and backend API polling
 * - RTMS state (isRtmsStartedByOther, rtmsRequesterName, rtmsStreamId) is managed by Home.jsx
 * - This component receives RTMS state as props and uses setState functions to update state in Home.jsx
 */

const SummarizeBox = ({ 
  meetingId, 
  meetingUuid, 
  aimsStatus, 
  role, 
  alias, 
  appContext, 
  hasOutlookWarning, 
  hasGermanyAttendee, 
  summarizeState, 
  setSummarizeState, 
  summarizeStateRef,
  isRestarting, 
  setIsRestarting,
  // RTMS state from Home.jsx (read-only)
  isRtmsStartedByOther,
  rtmsRequesterName,
  rtmsStreamId,
  // setState functions to update RTMS state in Home.jsx
  setIsRtmsStartedByOther,
  setRtmsRequesterName,
  setRtmsStreamId,
  isRtmsStartedByOtherRef
}) => {
  const navigate = useNavigate();
  
  // Modal states
  const [isInformSummarizeModalOpen, setIsInformSummarizeModalOpen] = useState(false);
  const [isServerErrorModalOpen, setIsServerErrorModalOpen] = useState(false);
  const [isRtmsErrorModalOpen, setIsRtmsErrorModalOpen] = useState(false);
  const [isProductFeedbackModalOpen, setIsProductFeedbackModalOpen] = useState(false);
  
  // Data states
  const [summarizeClickCount, setSummarizeClickCount] = useState(0);
  const [aimsUuid, setAimsUuid] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [rtmsErrorType, setRtmsErrorType] = useState(null);
  const [summaryReviewers, setSummaryReviewers] = useState(null);

  
  // Reference to store the timeout for hiding the SummarizeStopped component
  const hideStoppedComponentTimerRef = useRef(null);
  // Reference to store the RTMS start timestamp to avoid state timing issues
  const rtmsStartTimestampRef = useRef(null);
  // References for polling session status after summarize click
  const sessionStatusIntervalRef = useRef(null);
  const sessionStatusTimeoutRef = useRef(null);
  // Reference for rejection timeout (2 minutes)
  const rejectionTimeoutRef = useRef(null);

  /**
   * Handle RTMS status change events from Zoom SDK
   * @param {object} event - The RTMS status change event
   */
  async function handleRTMSChange(event) {
    LogManager.info("RTMS changed:", JSON.stringify(event));
    LogManager.info(`[SummarizeBox] RTMS status changed to: ${event.status}`);
    
    if (event.status && event.status === "started") {
      // Check who started the session by calling the session status API
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        LogManager.info("Session status when started:", sessionStatus);
        
        // Save the rtmsStreamId when RTMS is active
        if (sessionStatus.rtmsStreamId) {
          setRtmsStreamId(sessionStatus.rtmsStreamId);
          LogManager.info("Saved rtmsStreamId:", sessionStatus.rtmsStreamId);
        }
        
        // Check if the session was started by someone else
        if (!sessionStatus.rtmsRequesterAlias) {
          // No requester info available - this is problematic, hide summarize button
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
        } else if (!alias) {
          // Current user's alias not available - treat as unknown
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else if (sessionStatus.rtmsRequesterAlias !== alias) {
          // Started by someone else - hide the summarize button
          setIsRtmsStartedByOther(true);
          setRtmsRequesterName(sessionStatus.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME);
        } else {
          // Started by current user - keep showing summarize button
          setIsRtmsStartedByOther(false);
        }
      } catch (error) {
        LogManager.error("Error fetching session status on RTMS start:", error);
        // Fallback: hide summarize button if we can't determine who started it
        setIsRtmsStartedByOther(true);
        setRtmsRequesterName(RTMS_REQUESTER_DEFAULT_NAME);
      }
      
      // Set summarizing state and record timestamp
      setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
      
      // Record the timestamp when summarization starts
      const startTimestamp = Math.floor(Date.now() / 1000);
      rtmsStartTimestampRef.current = startTimestamp;
      LogManager.info("RTMS started at timestamp:", startTimestamp);
    } else if (event.status && event.status === "stopped") {
      // Show product feedback modal when RTMS stops
      setIsProductFeedbackModalOpen(true);
      
      // Reset isRtmsStartedByOther immediately so organizers and alt hosts can see the summary button
      setIsRtmsStartedByOther(false);
      setRtmsRequesterName(null);
      
      // Set to STOPPED state immediately
      setSummarizeState(SUMMARIZE_STATES.STOPPED);
      
      // Fetch session status to check for errors and get reviewer data
      fetchRtmsSessionStatus(meetingUuid).then((sessionData) => {
        LogManager.info(`Session status: ${sessionData.status}, statusMessage: ${sessionData.statusMessage}`);
        
        // Extract reviewer data from session API response
        if (sessionData.reviewers) {
          LogManager.info("Summary reviewer data from session API:", sessionData.reviewers);
          setSummaryReviewers(sessionData.reviewers);
        }
        
        if (sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
            sessionData.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
          // Show error modal for init or runtime errors only if current user started RTMS
          if (!isRtmsStartedByOtherRef.current) {
            setRtmsErrorType(sessionData.statusMessage);
            setIsRtmsErrorModalOpen(true);
          }
        } else {
          // Start the summary checking process after RTMS stops
          setTimeout(async () => {
            const queryStartTime = rtmsStartTimestampRef.current - 10; 
            const queryEndTime = rtmsStartTimestampRef.current + 60;
            
            LogManager.info(`Using rtmsStartTimestamp: ${rtmsStartTimestampRef.current} for summary query`);
            const found = await checkForSummary(queryStartTime, queryEndTime);
            if (!found) {
              clearTimeout(hideStoppedComponentTimerRef.current);
              hideStoppedComponentTimerRef.current = setTimeout(() => {
                if (summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING) {
                  setSummarizeState(SUMMARIZE_STATES.DEFAULT);
                }
              }, 120000); // 2 minutes from now
            }
          }, 60000); // 1 minute
          
          // Show SummaryStopped component for 2 minutes (if summary not found)
          hideStoppedComponentTimerRef.current = setTimeout(() => {
            if (summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING) {
              setSummarizeState(SUMMARIZE_STATES.DEFAULT);
            }
          }, 60000);
        }
      }).catch(error => {
        LogManager.error("Error fetching session status:", error);
        // Fallback to normal behavior if status fetch fails
        setSummarizeState(SUMMARIZE_STATES.STOPPED);
      });
    }
  }

  // Auto-clear isRestarting when we leave LOADING state
  useEffect(() => {
    if (summarizeState !== SUMMARIZE_STATES.LOADING && isRestarting) {
      setIsRestarting(false);
    }
  }, [summarizeState]);

  useEffect(() => {
    // Localhost testing override
    if (window.location.href === "http://localhost:3000/") {
      setIsInformSummarizeModalOpen(false);
      setSummarizeState(SUMMARIZE_STATES.DEFAULT);
      setIsServerErrorModalOpen(false);
    } else {
      // Set up RTMS status change listener for non-localhost environments
      try {
        LogManager.info(`[SummarizeBox] Setting up RTMS status change listener for meeting: ${meetingUuid}`);
        zoomSdk.onRTMSStatusChange(handleRTMSChange);
      } catch (error) {
        LogManager.error("[SummarizeBox] Error setting up RTMS status listener:", error);
      }
    }
    
    // Cleanup function to clear the timeout when component unmounts
    return () => {
      if (hideStoppedComponentTimerRef.current) {
        clearTimeout(hideStoppedComponentTimerRef.current);
      }
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }
      if (rejectionTimeoutRef.current) {
        clearTimeout(rejectionTimeoutRef.current);
      }
    };
  }, [meetingUuid]);

  // Function to check for summary after RTMS has ended
  const checkForSummary = async (queryStartTime, queryEndTime) => {
    LogManager.info(`Checking for summary with time between ${queryStartTime} and ${queryEndTime}`);
    
    try {
      // Override the default time range in fetchSummaries with our specific time range
      const summaryListJson = await fetchSummaries(queryStartTime, queryEndTime, 1);
      
      if (summaryListJson && summaryListJson.summaries && summaryListJson.summaries.length > 0) {
        // Found a summary that matches our criteria
        const summary = summaryListJson.summaries[0];
        if (summary.meetingUuid === meetingUuid && summarizeStateRef.current !== SUMMARIZE_STATES.SUMMARIZING) {
          LogManager.info("Found matching summary:", summary.aimsUuid);
          setAimsUuid(summary.aimsUuid);
          setSummaryData(summary);
          setSummarizeState(SUMMARIZE_STATES.READY);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      LogManager.error("Error checking for summary:", error);
      return false;
    }
  };

  //TODO: add more checks
  const onEndSummarize = async () => {
    if (summarizeState === SUMMARIZE_STATES.LOADING) return;
    setSummarizeState(SUMMARIZE_STATES.LOADING);
    LogManager.info("End button clicked, call stopping RTMS API...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating stop RTMS call.");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK"; // or "Fail" to simulate error
      // Note: State changes will be handled by handleRTMSChange event listener
    } else {
      try {
        rtmsResponse = await fetchRtmsStatusUpdate("stop", meetingUuid, meetingId, role, appContext);
      } catch (e) {
        console.error(e);
        rtmsResponse = "Error";
      }
    }

    if (rtmsResponse.result !== "OK") {
      setSummarizeState(SUMMARIZE_STATES.DEFAULT); // Reset to default state on error
      LogManager.info(`RTMS stop request failed with result: ${rtmsResponse.result}, showing server error modal`);
      setIsServerErrorModalOpen(true);
      return;
    }

    // Note: State changes (summarizeState updates, summary checking) 
    // are now handled automatically by the handleRTMSChange event listener
    // Loading state will be cleared when RTMS status change event is received
    LogManager.info("RTMS stop request successful. State changes will be handled by event listener.");

    // Don't clear loading state here for successful requests - let handleRTMSChange do it
  };

  const onModalConfirmed = () => {
    setIsInformSummarizeModalOpen(false);
    onSummarize();
  };

  const handleRtmsErrorCancel = () => {
    // Cancel: Close modal and reset to original state (show Summarize button)
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset all states to original
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    // Clear requester information
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User cancelled RTMS error modal - reset to original state");
  };

  const handleRtmsErrorRestart = () => {
    // Restart: Close modal and restart summarization
    setIsRtmsErrorModalOpen(false);
    setRtmsErrorType(null);
    // Reset states and restart
    setSummarizeState(SUMMARIZE_STATES.DEFAULT);
    // Mark as restarting
    setIsRestarting(true);
    // Clear requester information before restarting
    setIsRtmsStartedByOther(false);
    setRtmsRequesterName(null);
    LogManager.info("User clicked restart from RTMS error modal - restarting summarization");
    // Start summarization immediately
    onSummarize(true);
  };

  //TODO: Add more checks
  const onSummarize = async (isRestartAttempt = false) => {
    if (summarizeState === SUMMARIZE_STATES.LOADING) return;
    setSummarizeState(SUMMARIZE_STATES.LOADING);
    LogManager.info("IsRestarting: " + isRestartAttempt);
    if (isRestartAttempt) {
      LogManager.info("User clicked restart - waiting 45 seconds for backend timeout before sending new request");
      
      // Wait 45 seconds (first attempt already waited 15s, total 60s for backend timeout)
      await new Promise(resolve => setTimeout(resolve, 45000));
      
      // Now send a fresh start request
      LogManager.info("45 seconds elapsed, sending new RTMS start request");
    }
    LogManager.info("Attempting to start RTMS...");

    let rtmsResponse = {};
    if (window.location.href === "http://localhost:3000/") {
      console.log("[Localhost] Simulating startRTMS call: Success");
      await new Promise(resolve => setTimeout(resolve, 2000)); // 2 seconds delay
      rtmsResponse.result = "OK";
    } else {
      try {
        rtmsResponse = await fetchRtmsStatusUpdate("start", meetingUuid, meetingId, role, appContext);
      } catch (e) {
        LogManager.error("Error calling /rtms-status-update:", e);
        rtmsResponse = "Error";
      }
    }

    LogManager.info("The /rtms-status-update response:", rtmsResponse);

    if (rtmsResponse.result === "OK") {
      setSummarizeClickCount((prev) => prev + 1);
      // Start polling session status every 5 seconds
      LogManager.info("RTMS start request successful. Starting session status polling...");
      
      // Clear any existing intervals/timeouts
      if (sessionStatusIntervalRef.current) {
        clearInterval(sessionStatusIntervalRef.current);
      }
      if (sessionStatusTimeoutRef.current) {
        clearTimeout(sessionStatusTimeoutRef.current);
      }
      
      // Set up polling every 5 seconds
      sessionStatusIntervalRef.current = setInterval(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          LogManager.info(`Session status polling result: ${sessionStatus.status}, statusMessage: ${sessionStatus.statusMessage}`);
          
          if (sessionStatus.status === SESSION_STATUS.ACTIVE) {
            // Status is now active, save rtmsStreamId and update state
            if (sessionStatus.rtmsStreamId) {
              setRtmsStreamId(sessionStatus.rtmsStreamId);
              LogManager.info("Saved rtmsStreamId during polling:", sessionStatus.rtmsStreamId);
            }
            
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            setSummarizeState(SUMMARIZE_STATES.SUMMARIZING);
            LogManager.info("Session status is now ACTIVE, stopping polling");
          } else if (sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR || 
                     sessionStatus.statusMessage === SESSION_STATUS_MESSAGE.RTMS_RUNTIME_ERROR) {
            // Error detected during polling, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            clearTimeout(sessionStatusTimeoutRef.current);
            setSummarizeState(SUMMARIZE_STATES.DEFAULT);
            setRtmsErrorType(sessionStatus.statusMessage);
            setIsRtmsErrorModalOpen(true);
            LogManager.info(`RTMS error detected during polling: ${sessionStatus.statusMessage}, showing error modal`);
          }
        } catch (error) {
          LogManager.error("Error polling session status:", error);
        }
      }, 5000);
      
      // Set up timeout to show error modal if still INACTIVE (15s for first attempt, 60s for restart)
      const timeoutDuration = isRestartAttempt ? 60000 : 15000;
      sessionStatusTimeoutRef.current = setTimeout(async () => {
        try {
          const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
          if (sessionStatus.status !== SESSION_STATUS.ACTIVE) {
            // Still inactive after timeout, show error modal
            clearInterval(sessionStatusIntervalRef.current);
            setSummarizeState(SUMMARIZE_STATES.DEFAULT);
            setRtmsErrorType(SESSION_STATUS_MESSAGE.RTMS_INIT_ERROR);
            setIsRtmsErrorModalOpen(true);
            LogManager.info(`Session status still INACTIVE after ${timeoutDuration/1000} seconds, showing error modal`);
          }
        } catch (error) {
          LogManager.error("Error checking final session status:", error);
          clearInterval(sessionStatusIntervalRef.current);
          setSummarizeState(SUMMARIZE_STATES.DEFAULT);
          setIsServerErrorModalOpen(true);
        }
      }, timeoutDuration);
    } else if (rtmsResponse.result === "REJECTED") {
      // Someone else already started the summary
      setSummarizeState(SUMMARIZE_STATES.REJECTED);
      const requesterName = rtmsResponse.rtmsRequesterName || RTMS_REQUESTER_DEFAULT_NAME;
      setRtmsRequesterName(requesterName);
      LogManager.info(`RTMS start rejected - ${requesterName} is already starting summary`);
      
      // Set up 2-minute timer to reset to Summarize button if no RTMS "started" event is received
      rejectionTimeoutRef.current = setTimeout(() => {
        LogManager.info("2-minute rejection timeout reached, resetting to Summarize button");
        setSummarizeState(SUMMARIZE_STATES.DEFAULT);
        setRtmsRequesterName(null);
      }, 120000); // 2 minutes
    } else {
      // Handle all other error cases (FAIL, Error, etc.)
      setSummarizeState(SUMMARIZE_STATES.DEFAULT); // Clear loading state on error
      setIsServerErrorModalOpen(false);
      setTimeout(() => setIsServerErrorModalOpen(true), 0);
    }
  };

  return (
    <Theme palette={aimsTheme}>
      <Column alignmentHorizontal="start" spacing="none" minHeight="630px" heights={["fit", "fill","fit"]}>
        <Box>
          <Column height="40px"/>
          {/* Hide the summarize button box if session is started by someone else */}
          {!isRtmsStartedByOther && (
            <Column height="100px" spacing="none">
              <Row width="100%" spacing="none" spacingInset="none">
                <Column width="72px"/>
                <Theme tokens={{ boxBorderColorOutline: "#E7E9E9" }}>
                  <Box type="outline" spacingInset="400" width="226px" height="100px">
                    <Row spacing="none">
                      <img src={star} alt="star"/>
                      <Column width="20px"/>
                      <Text fontFamily="amazonEmber" type="h50" alignment="start">
                        Summarize meeting
                      </Text>
                    </Row>
                    <Column height="12px"></Column>
                    <Theme
                      tokens={{
                        buttonBackgroundColorPrimaryDefault: summarizeState === SUMMARIZE_STATES.SUMMARIZING ? "#AF1F18" : "#006D5B",
                        buttonBackgroundColorPrimaryHover: summarizeState === SUMMARIZE_STATES.SUMMARIZING ? "#8C1913" : "#005345",
                        buttonBackgroundColorPrimaryPressed: summarizeState === SUMMARIZE_STATES.SUMMARIZING ? "#6E140F" : "#003F33",
                      }}
                    >
                      {summarizeState === SUMMARIZE_STATES.LOADING ? (
                        <Row alignmentHorizontal="center">
                          <Theme palette={aimsTheme}>
                            <Loader size="medium"/>
                          </Theme>
                        </Row>
                      ) : summarizeState === SUMMARIZE_STATES.SUMMARIZING ? (
                        <Button size="small" minWidth="194px" onClick={onEndSummarize}>
                          End
                        </Button>
                      ) : summarizeState === SUMMARIZE_STATES.REJECTED ? (
                        <Row alignmentHorizontal="center">
                          <img src={errorIcon} alt="error" style={{ display: "block", margin: "auto", width: "25.6px", height: "auto" }}/>
                        </Row>
                      ) : (
                        // DEFAULT, READY, STOPPED states all show the Summarize button
                        <Button
                          size="small"
                          minWidth="194px"
                          onClick={() => {
                            if (summarizeClickCount === 0) {
                              setIsInformSummarizeModalOpen(true);
                            } else {
                              onSummarize(); // second+ click: directly start summarize
                            }
                          }}
                        >
                          Summarize
                        </Button>
                      )}
                    </Theme>
                    {isServerErrorModalOpen ? (
                      <ServerNotRespondingModal isOpen={true} onClose={() => setIsServerErrorModalOpen(false)} />
                    ) : (
                      <InformSummarizeModal
                        isOpen={isInformSummarizeModalOpen}
                        onClose={() => setIsInformSummarizeModalOpen(false)}
                        onConfirm={onModalConfirmed}
                      />
                    )}
                    {isRtmsErrorModalOpen &&
                      <RtmsErrorModal
                        isOpen={isRtmsErrorModalOpen}
                        onCancel={handleRtmsErrorCancel}
                        onRestart={handleRtmsErrorRestart}
                        errorType={rtmsErrorType}
                      />
                    }
                  </Box>
                </Theme>
              </Row>
            </Column>
          )}
          <Column height="32px"/>
          {/* SummarizeBoxIndicator: Handles all state-specific UI displays (warnings, status messages, etc.) */}
          {/* This is separate from the main SummarizeBox button functionality above */}
          <SummarizeBoxIndicator 
            summarizeState={summarizeState}
            rtmsRequesterName={rtmsRequesterName}
            hasOutlookWarning={hasOutlookWarning}
            aimsStatus={aimsStatus}
            role={role}
            summaryReviewers={summaryReviewers}
            alias={alias}
            aimsUuid={aimsUuid}
            summaryData={summaryData}
            rtmsStreamId={rtmsStreamId}
            meetingUuid={meetingUuid}
            meetingId={meetingId}
            isRtmsStartedByOther={isRtmsStartedByOther}
            isRestarting={isRestarting}
            hasGermanyAttendee={hasGermanyAttendee}
            onViewSummary={() => {
              // Clear the timer when navigating to the summary page
              if (hideStoppedComponentTimerRef.current) {
                clearTimeout(hideStoppedComponentTimerRef.current);
                hideStoppedComponentTimerRef.current = null;
              }
              navigate(`/meeting/summary/${aimsUuid}`, { state: { summary: summaryData } });
            }}
          />
        </Box>
        <Box/>
        <Box width="358px">
          <ProductFeedback 
            isModalOpen={isProductFeedbackModalOpen} 
            setIsModalOpen={setIsProductFeedbackModalOpen} 
            usePopover={true}
            page={FEEDBACK_PAGE.SUMMARIZE_END}
          />
        </Box>
      </Column>
    </Theme>
  );
};

export default SummarizeBox;
